// InvoicePage.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Eye, FileText, Mail, MessageSquare,
  Download, Search, Filter, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Clock, DollarSign, User, Calendar,
  Printer, Share2, MoreVertical, ShoppingCart, CreditCard, 
  IndianRupee, Receipt, FileSpreadsheet, TrendingUp, Bell,
  Send, ExternalLink, Copy, DownloadCloud, Upload
} from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const InvoicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Store ID from localStorage
  const [storeId, setStoreId] = useState(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedData = JSON.parse(userData);
      return parsedData.storeIds?.[0] || null;
    }
    return null;
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Jan 1
    endDate: new Date().toISOString().split('T')[0] // Today
  });
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Form states
  const [invoiceForm, setInvoiceForm] = useState({
    storeId: '',
    customerId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    discountAmount: 0,
    roundOff: 0,
    paymentMode: 'BANK',
    termsConditions: 'Payment due within 15 days',
    notes: '',
    items: [{
      productId: '',
      itemName: '',
      hsnSacCode: '',
      unit: 'PCS',
      quantity: 1,
      unitPrice: 0,
      cgstRate: 0,
      sgstRate: 0,
      igstRate: 0,
      discountRate: 0
    }]
  });
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    paymentMode: 'BANK',
    paymentStatus: 'PAID',
    paymentTransactionId: '',
    amountPaid: 0,
    balanceDue: 0,
    paymentDate: new Date().toISOString().split('T')[0]
  });
  
  // Share form
  const [emailAddress, setEmailAddress] = useState('');
  
  // Validation
  useEffect(() => {
    if (!storeId || !user?.storeIds.includes(parseInt(storeId))) {
      console.log('Invalid storeId or unauthorized:', { storeId, userStoreIds: user?.storeIds });
      navigate('/unauthorized');
      return;
    }
    fetchInvoices();
    fetchCustomers();
    fetchProducts();
  }, [storeId, user, navigate, currentPage, pageSize]);

  // Fetch invoices
  const fetchInvoices = async () => {
    if (!storeId) return;
    
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `/invoices?storeId=${storeId}&page=${currentPage}&size=${pageSize}&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`, 
        {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }
      );
      
      console.log('Invoices response:', response.data);
      
      if (response.data.success) {
        setInvoices(response.data.data.content || []);
        setTotalPages(response.data.data.totalPages || 1);
        setTotalElements(response.data.data.totalElements || 0);
      }
    } catch (err) {
      console.error('Fetch invoices error:', err);
      if (err.response?.status === 401) navigate('/login');
      setError(err.response?.data?.message || err.message || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch customers
  const fetchCustomers = async () => {
    if (!storeId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/customers/by-store/${storeId}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      
      if (response.data.data?.content) {
        setCustomers(response.data.data.content);
      } else if (Array.isArray(response.data)) {
        setCustomers(response.data);
      }
    } catch (err) {
      console.error('Fetch customers error:', err);
    }
  };
  
  // Fetch products
  const fetchProducts = async () => {
    if (!storeId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/products?storeId=${storeId}&size=100`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      
      if (response.data.success) {
        setProducts(response.data.data.content || []);
      }
    } catch (err) {
      console.error('Fetch products error:', err);
    }
  };
  
  // Create invoice
  const createInvoice = async () => {
    if (!storeId) {
      setError('No store selected');
      return;
    }
    
    // Validate required fields
    if (!invoiceForm.customerId || invoiceForm.items.length === 0) {
      setError('Please select a customer and add at least one item');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.post('/invoices', {
        ...invoiceForm,
        storeId: parseInt(storeId),
        customerId: parseInt(invoiceForm.customerId),
        discountAmount: parseFloat(invoiceForm.discountAmount) || 0,
        roundOff: parseFloat(invoiceForm.roundOff) || 0,
        items: invoiceForm.items.map(item => ({
          productId: item.productId ? parseInt(item.productId) : null,
          itemName: item.itemName,
          hsnSacCode: item.hsnSacCode,
          unit: item.unit,
          quantity: parseInt(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          cgstRate: parseFloat(item.cgstRate) || 0,
          sgstRate: parseFloat(item.sgstRate) || 0,
          igstRate: parseFloat(item.igstRate) || 0,
          discountRate: parseFloat(item.discountRate) || 0
        }))
      }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.data.success) {
        setSuccessMessage('Invoice created successfully!');
        fetchInvoices();
        resetForm();
        setShowCreateModal(false);
      }
    } catch (err) {
      console.error('Create invoice error:', err);
      if (err.response?.status === 401) navigate('/login');
      setError(err.response?.data?.message || err.message || 'Failed to create invoice');
    }
  };
  
  // Update invoice
  const updateInvoice = async () => {
    if (!selectedInvoice) {
      setError('No invoice selected');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.put(`/invoices/${selectedInvoice.id}`, {
        storeId: parseInt(storeId),
        customerId: parseInt(invoiceForm.customerId),
        invoiceDate: invoiceForm.invoiceDate,
        dueDate: invoiceForm.dueDate,
        discountAmount: parseFloat(invoiceForm.discountAmount) || 0,
        roundOff: parseFloat(invoiceForm.roundOff) || 0,
        paymentMode: invoiceForm.paymentMode,
        termsConditions: invoiceForm.termsConditions,
        notes: invoiceForm.notes,
        items: invoiceForm.items.map(item => ({
          productId: item.productId ? parseInt(item.productId) : null,
          itemName: item.itemName,
          hsnSacCode: item.hsnSacCode,
          unit: item.unit,
          quantity: parseInt(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          cgstRate: parseFloat(item.cgstRate) || 0,
          sgstRate: parseFloat(item.sgstRate) || 0,
          igstRate: parseFloat(item.igstRate) || 0,
          discountRate: parseFloat(item.discountRate) || 0
        }))
      }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.data.success) {
        setSuccessMessage('Invoice updated successfully!');
        fetchInvoices();
        resetForm();
        setShowEditModal(false);
        setSelectedInvoice(null);
      }
    } catch (err) {
      console.error('Update invoice error:', err);
      if (err.response?.status === 401) navigate('/login');
      setError(err.response?.data?.message || err.message || 'Failed to update invoice');
    }
  };
  
  // Delete invoice
  const deleteInvoice = async () => {
    if (!selectedInvoice) {
      setError('No invoice selected');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/invoices/${selectedInvoice.id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      
      setSuccessMessage('Invoice deleted successfully!');
      fetchInvoices();
      setShowDeleteModal(false);
      setSelectedInvoice(null);
    } catch (err) {
      console.error('Delete invoice error:', err);
      if (err.response?.status === 401) navigate('/login');
      setError(err.response?.data?.message || err.message || 'Failed to delete invoice');
    }
  };
  
  // Update invoice status
  const updateInvoiceStatus = async (status) => {
    if (!selectedInvoice) {
      setError('No invoice selected');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.put(`/invoices/${selectedInvoice.id}/status?status=${status}`, {}, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      
      if (response.data.success) {
        setSuccessMessage(`Invoice status updated to ${status}!`);
        fetchInvoices();
        setShowStatusModal(false);
        setSelectedInvoice(null);
      }
    } catch (err) {
      console.error('Update status error:', err);
      if (err.response?.status === 401) navigate('/login');
      setError(err.response?.data?.message || err.message || 'Failed to update invoice status');
    }
  };
  
  // Update payment
  const updatePayment = async () => {
    if (!selectedInvoice) {
      setError('No invoice selected');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.put(`/invoices/${selectedInvoice.id}/payment`, {
        paymentMode: paymentForm.paymentMode,
        paymentStatus: paymentForm.paymentStatus,
        paymentTransactionId: paymentForm.paymentTransactionId,
        amountPaid: parseFloat(paymentForm.amountPaid) || 0,
        balanceDue: parseFloat(paymentForm.balanceDue) || 0,
        paymentDate: paymentForm.paymentDate
      }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.data.success) {
        setSuccessMessage('Payment updated successfully!');
        fetchInvoices();
        setShowPaymentModal(false);
        setSelectedInvoice(null);
        setPaymentForm({
          paymentMode: 'BANK',
          paymentStatus: 'PAID',
          paymentTransactionId: '',
          amountPaid: 0,
          balanceDue: 0,
          paymentDate: new Date().toISOString().split('T')[0]
        });
      }
    } catch (err) {
      console.error('Update payment error:', err);
      if (err.response?.status === 401) navigate('/login');
      setError(err.response?.data?.message || err.message || 'Failed to update payment');
    }
  };
  
  // Generate PDF
  const generatePDF = async (invoice) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/invoices/${invoice.id}/pdf`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccessMessage('Invoice PDF downloaded successfully!');
    } catch (err) {
      console.error('Download PDF error:', err);
      setError('PDF generation is currently unavailable');
    }
  };
  
  // Send email
  const sendEmail = async (invoice, email) => {
    if (!email) {
      setError('Please enter an email address');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/invoices/${invoice.id}/send-email?email=${encodeURIComponent(email)}`, {}, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      
      if (response.data.success) {
        setSuccessMessage(`Invoice sent to ${email} successfully!`);
        setShowShareModal(false);
        setEmailAddress('');
      }
    } catch (err) {
      console.error('Send email error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to send invoice email');
    }
  };
  
  // Send WhatsApp
  const sendWhatsApp = async (invoice) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/invoices/${invoice.id}/send-whatsapp`, {}, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      
      if (response.data.success) {
        setSuccessMessage('Invoice sent via WhatsApp successfully!');
      }
    } catch (err) {
      console.error('Send WhatsApp error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to send invoice via WhatsApp');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setInvoiceForm({
      storeId: '',
      customerId: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      discountAmount: 0,
      roundOff: 0,
      paymentMode: 'BANK',
      termsConditions: 'Payment due within 15 days',
      notes: '',
      items: [{
        productId: '',
        itemName: '',
        hsnSacCode: '',
        unit: 'PCS',
        quantity: 1,
        unitPrice: 0,
        cgstRate: 0,
        sgstRate: 0,
        igstRate: 0,
        discountRate: 0
      }]
    });
    setSelectedInvoice(null);
  };
  
  // Add item
  const addItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, {
        productId: '',
        itemName: '',
        hsnSacCode: '',
        unit: 'PCS',
        quantity: 1,
        unitPrice: 0,
        cgstRate: 0,
        sgstRate: 0,
        igstRate: 0,
        discountRate: 0
      }]
    });
  };
  
  // Remove item
  const removeItem = (index) => {
    const newItems = [...invoiceForm.items];
    newItems.splice(index, 1);
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };
  
  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // If product is selected, auto-fill details
    if (field === 'productId' && value) {
      const selectedProduct = products.find(p => p.id === parseInt(value));
      if (selectedProduct) {
        newItems[index].itemName = selectedProduct.name;
        newItems[index].unitPrice = selectedProduct.sellingPrice;
        newItems[index].unit = selectedProduct.unit || 'PCS';
        // You might want to add default GST rates from product
      }
    }
    
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };
  
  // Calculate item totals
  const calculateItemTotal = (item) => {
    const baseAmount = item.quantity * item.unitPrice;
    const discountAmount = baseAmount * (item.discountRate / 100);
    const taxableAmount = baseAmount - discountAmount;
    const cgst = taxableAmount * (item.cgstRate / 100);
    const sgst = taxableAmount * (item.sgstRate / 100);
    const igst = taxableAmount * (item.igstRate / 100);
    return {
      baseAmount,
      discountAmount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      total: taxableAmount + cgst + sgst + igst
    };
  };
  
  // Calculate invoice totals
  const calculateInvoiceTotals = () => {
    const itemTotals = invoiceForm.items.map(calculateItemTotal);
    const subtotal = itemTotals.reduce((sum, item) => sum + item.taxableAmount, 0);
    const totalCGST = itemTotals.reduce((sum, item) => sum + item.cgst, 0);
    const totalSGST = itemTotals.reduce((sum, item) => sum + item.sgst, 0);
    const totalIGST = itemTotals.reduce((sum, item) => sum + item.igst, 0);
    const totalDiscount = itemTotals.reduce((sum, item) => sum + item.discountAmount, 0) + parseFloat(invoiceForm.discountAmount || 0);
    const grandTotal = subtotal + totalCGST + totalSGST + totalIGST - parseFloat(invoiceForm.discountAmount || 0) + parseFloat(invoiceForm.roundOff || 0);
    
    return {
      subtotal,
      totalCGST,
      totalSGST,
      totalIGST,
      totalDiscount,
      grandTotal
    };
  };
  
  // Open edit modal
  const openEditModal = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceForm({
      storeId: invoice.storeId?.toString() || storeId?.toString(),
      customerId: invoice.customerId?.toString() || '',
      invoiceDate: invoice.invoiceDate?.split('T')[0] || new Date().toISOString().split('T')[0],
      dueDate: invoice.dueDate?.split('T')[0] || new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      discountAmount: invoice.discountAmount || 0,
      roundOff: invoice.roundOff || 0,
      paymentMode: invoice.paymentMode || 'BANK',
      termsConditions: invoice.termsConditions || 'Payment due within 15 days',
      notes: invoice.notes || '',
      items: invoice.items?.map(item => ({
        productId: item.productId?.toString() || '',
        itemName: item.itemName || '',
        hsnSacCode: item.hsnSacCode || '',
        unit: item.unit || 'PCS',
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || 0,
        cgstRate: item.cgstRate || 0,
        sgstRate: item.sgstRate || 0,
        igstRate: item.igstRate || 0,
        discountRate: item.discountRate || 0
      })) || [{
        productId: '',
        itemName: '',
        hsnSacCode: '',
        unit: 'PCS',
        quantity: 1,
        unitPrice: 0,
        cgstRate: 0,
        sgstRate: 0,
        igstRate: 0,
        discountRate: 0
      }]
    });
    setShowEditModal(true);
  };
  
  // Open payment modal
  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      paymentMode: invoice.paymentMode || 'BANK',
      paymentStatus: invoice.paymentStatus || 'UNPAID',
      paymentTransactionId: invoice.paymentTransactionId || '',
      amountPaid: invoice.amountPaid || 0,
      balanceDue: invoice.balanceDue || invoice.totalAmount,
      paymentDate: new Date().toISOString().split('T')[0]
    });
    setShowPaymentModal(true);
  };
  
  // Open share modal
  const openShareModal = (invoice) => {
    setSelectedInvoice(invoice);
    setEmailAddress('');
    setShowShareModal(true);
  };
  
  // View invoice
  const viewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };
  
  // Clear messages
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);
  
  // Status badge color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'PAID': return 'bg-green-100 text-green-800';
      case 'UNPAID': return 'bg-red-100 text-red-800';
      case 'PARTIALLY_PAID': return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Payment status color
  const getPaymentStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID': return 'bg-green-100 text-green-800';
      case 'UNPAID': return 'bg-red-100 text-red-800';
      case 'PARTIALLY_PAID': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = !searchTerm || 
      (invoice.invoiceNumber && invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (invoice.customerName && invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'ALL' || invoice.status === statusFilter;
    const matchesPaymentStatus = paymentStatusFilter === 'ALL' || invoice.paymentStatus === paymentStatusFilter;
    
    return matchesSearch && matchesStatus && matchesPaymentStatus;
  });
  
  // Calculate statistics
  const totalInvoices = invoices.length;
  const paidInvoices = invoices.filter(i => i.paymentStatus === 'PAID').length;
  const unpaidInvoices = invoices.filter(i => i.paymentStatus === 'UNPAID').length;
  const partiallyPaid = invoices.filter(i => i.paymentStatus === 'PARTIALLY_PAID').length;
  const totalRevenue = invoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
  const totalPaid = invoices.reduce((sum, invoice) => sum + (invoice.amountPaid || 0), 0);
  const totalDue = invoices.reduce((sum, invoice) => sum + (invoice.balanceDue || 0), 0);
  const overdueInvoices = invoices.filter(i => 
    i.paymentStatus !== 'PAID' && 
    i.dueDate && 
    new Date(i.dueDate) < new Date()
  ).length;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Invoice Management</h1>
            <p className="text-gray-600 mt-1">Store ID: {storeId} | Manage your invoices and billing</p>
          </div>
          <div className="flex items-center space-x-3 mt-4 md:mt-0">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </button>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Invoices</p>
                <p className="text-2xl font-bold">{totalInvoices}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Paid</p>
                <p className="text-2xl font-bold">{paidInvoices}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <XCircle className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Unpaid</p>
                <p className="text-2xl font-bold">{unpaidInvoices}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <DollarSign className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-xl font-bold">₹{totalRevenue.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <Bell className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold">{overdueInvoices}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Messages */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
          {successMessage}
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by invoice number or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="PAID">Paid</option>
              <option value="UNPAID">Unpaid</option>
            </select>
          </div>
          
          <div>
            <select
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Payment Status</option>
              <option value="PAID">Paid</option>
              <option value="UNPAID">Unpaid</option>
              <option value="PARTIALLY_PAID">Partially Paid</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={fetchInvoices}
              className="p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-lg"
              title="Apply Filters"
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredInvoices.length} of {totalElements} invoices
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchInvoices}
              className="flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>
      
      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading invoices...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date / Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => {
                    const isOverdue = invoice.paymentStatus !== 'PAID' && invoice.dueDate && new Date(invoice.dueDate) < new Date();
                    
                    return (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </span>
                            <span className="text-xs text-gray-500">
                              {invoice.financialYear}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {invoice.customerName || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-600">
                                {new Date(invoice.invoiceDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center mt-1">
                              <Calendar className={`w-4 h-4 mr-2 ${isOverdue ? 'text-red-500' : 'text-gray-400'}`} />
                              <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                Due: {new Date(invoice.dueDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              ₹{invoice.totalAmount?.toLocaleString('en-IN') || '0'}
                            </span>
                            <div className="flex items-center text-xs text-gray-500 mt-1">
                              <span className="mr-2">Paid: ₹{invoice.amountPaid?.toLocaleString('en-IN') || '0'}</span>
                              <span>Due: ₹{invoice.balanceDue?.toLocaleString('en-IN') || invoice.totalAmount?.toLocaleString('en-IN') || '0'}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                              {invoice.status || 'DRAFT'}
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(invoice.paymentStatus)}`}>
                              {invoice.paymentStatus || 'UNPAID'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => viewInvoice(invoice)}
                              className="p-1 text-blue-600 hover:text-blue-800"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => generatePDF(invoice)}
                              className="p-1 text-green-600 hover:text-green-800"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEditModal(invoice)}
                              className="p-1 text-orange-600 hover:text-orange-800"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openPaymentModal(invoice)}
                              className="p-1 text-purple-600 hover:text-purple-800"
                              title="Update Payment"
                            >
                              <CreditCard className="w-4 h-4" />
                            </button>
                            <div className="relative">
                              <button
                                className="p-1 text-gray-600 hover:text-gray-800"
                                title="More Options"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-10 hidden group-hover:block">
                                <div className="py-1">
                                  <button
                                    onClick={() => updateInvoiceStatus('PAID')}
                                    className="block w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-gray-100"
                                  >
                                    Mark as Paid
                                  </button>
                                  <button
                                    onClick={() => openShareModal(invoice)}
                                    className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                                  >
                                    Send via Email
                                  </button>
                                  <button
                                    onClick={() => sendWhatsApp(invoice)}
                                    className="block w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-gray-100"
                                  >
                                    Send via WhatsApp
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedInvoice(invoice);
                                      setShowDeleteModal(true);
                                    }}
                                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Empty State */}
            {filteredInvoices.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No invoices found</h3>
                <p className="mt-1 text-gray-500">Get started by creating your first invoice.</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Pagination */}
      {filteredInvoices.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage * pageSize) + 1}</span> to{' '}
            <span className="font-medium">
              {Math.min((currentPage + 1) * pageSize, totalElements)}
            </span>{' '}
            of <span className="font-medium">{totalElements}</span> results
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className={`p-2 rounded-lg ${
                currentPage === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i;
              else if (currentPage <= 2) pageNum = i;
              else if (currentPage >= totalPages - 3) pageNum = totalPages - 5 + i;
              else pageNum = currentPage - 2 + i;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 rounded-lg ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-lg ${
                currentPage === totalPages - 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
      
      {/* Create Invoice Modal */}
      {showCreateModal && (
        <InvoiceFormModal
          title="Create New Invoice"
          form={invoiceForm}
          setForm={setInvoiceForm}
          customers={customers}
          products={products}
          storeId={storeId}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          onSubmit={createInvoice}
          addItem={addItem}
          removeItem={removeItem}
          updateItem={updateItem}
          calculateTotals={calculateInvoiceTotals}
        />
      )}
      
      {/* Edit Invoice Modal */}
      {showEditModal && selectedInvoice && (
        <InvoiceFormModal
          title="Edit Invoice"
          form={invoiceForm}
          setForm={setInvoiceForm}
          customers={customers}
          products={products}
          storeId={storeId}
          onClose={() => {
            setShowEditModal(false);
            resetForm();
          }}
          onSubmit={updateInvoice}
          addItem={addItem}
          removeItem={removeItem}
          updateItem={updateItem}
          calculateTotals={calculateInvoiceTotals}
        />
      )}
      
      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <ViewInvoiceModal
          invoice={selectedInvoice}
          onClose={() => setShowViewModal(false)}
          onDownloadPDF={generatePDF}
        />
      )}
      
      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <PaymentModal
          form={paymentForm}
          setForm={setPaymentForm}
          invoice={selectedInvoice}
          onClose={() => setShowPaymentModal(false)}
          onSubmit={updatePayment}
        />
      )}
      
      {/* Delete Modal */}
      {showDeleteModal && selectedInvoice && (
        <ConfirmModal
          title="Delete Invoice"
          message={`Are you sure you want to delete invoice ${selectedInvoice.invoiceNumber}? This action cannot be undone.`}
          onConfirm={deleteInvoice}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
      
      {/* Share Modal */}
      {showShareModal && selectedInvoice && (
        <ShareModal
          email={emailAddress}
          setEmail={setEmailAddress}
          invoice={selectedInvoice}
          onClose={() => setShowShareModal(false)}
          onSendEmail={sendEmail}
          onSendWhatsApp={sendWhatsApp}
        />
      )}
    </div>
  );
};

// Invoice Form Modal Component
const InvoiceFormModal = ({ 
  title, form, setForm, customers, products, storeId, 
  onClose, onSubmit, addItem, removeItem, updateItem, calculateTotals 
}) => {
  const totals = calculateTotals();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Customer Info */}
            <div className="md:col-span-2">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Customer Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                    <select
                      value={form.customerId}
                      onChange={(e) => setForm({...form, customerId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Customer</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name} - {customer.email || customer.phone}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Store ID</label>
                    <input
                      type="text"
                      value={storeId}
                      disabled
                      className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Invoice Details */}
            <div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Invoice Details</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date *</label>
                    <input
                      type="date"
                      value={form.invoiceDate}
                      onChange={(e) => setForm({...form, invoiceDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                    <input
                      type="date"
                      value={form.dueDate}
                      onChange={(e) => setForm({...form, dueDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Items Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900">Invoice Items</h4>
              <button
                onClick={addItem}
                className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="w-3 h-3 mr-1" /> Add Item
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">HSN/SAC</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">CGST%</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SGST%</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">IGST%</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Disc%</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {form.items.map((item, index) => {
                    const itemTotal = item.quantity * item.unitPrice;
                    const discount = itemTotal * (item.discountRate / 100);
                    const taxable = itemTotal - discount;
                    const cgst = taxable * (item.cgstRate / 100);
                    const sgst = taxable * (item.sgstRate / 100);
                    const igst = taxable * (item.igstRate / 100);
                    const total = taxable + cgst + sgst + igst;
                    
                    return (
                      <tr key={index}>
                        <td className="px-3 py-2">
                          <select
                            value={item.productId}
                            onChange={(e) => updateItem(index, 'productId', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="">Select Product</option>
                            {products.map(product => (
                              <option key={product.id} value={product.id}>
                                {product.name}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={item.itemName}
                            onChange={(e) => updateItem(index, 'itemName', e.target.value)}
                            placeholder="Item Name"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded mt-1"
                            required
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={item.hsnSacCode}
                            onChange={(e) => updateItem(index, 'hsnSacCode', e.target.value)}
                            placeholder="HSN/SAC"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={item.unit}
                            onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            <option value="PCS">PCS</option>
                            <option value="KG">KG</option>
                            <option value="L">L</option>
                            <option value="M">M</option>
                            <option value="BOX">BOX</option>
                            <option value="HRS">HRS</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            min="1"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={item.cgstRate}
                            onChange={(e) => updateItem(index, 'cgstRate', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={item.sgstRate}
                            onChange={(e) => updateItem(index, 'sgstRate', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={item.igstRate}
                            onChange={(e) => updateItem(index, 'igstRate', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={item.discountRate}
                            onChange={(e) => updateItem(index, 'discountRate', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-sm font-medium">₹{total.toFixed(2)}</span>
                        </td>
                        <td className="px-3 py-2">
                          {form.items.length > 1 && (
                            <button
                              onClick={() => removeItem(index)}
                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Summary Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="md:col-span-2">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Additional Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                    <select
                      value={form.paymentMode}
                      onChange={(e) => setForm({...form, paymentMode: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="BANK">Bank Transfer</option>
                      <option value="CASH">Cash</option>
                      <option value="CHEQUE">Cheque</option>
                      <option value="CARD">Card</option>
                      <option value="UPI">UPI</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                    <textarea
                      value={form.termsConditions}
                      onChange={(e) => setForm({...form, termsConditions: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={form.notes}
                      onChange={(e) => setForm({...form, notes: e.target.value})}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Totals */}
            <div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Invoice Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">₹{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">CGST:</span>
                    <span className="font-medium">₹{totals.totalCGST.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">SGST:</span>
                    <span className="font-medium">₹{totals.totalSGST.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">IGST:</span>
                    <span className="font-medium">₹{totals.totalIGST.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount:</span>
                    <span className="font-medium text-red-600">-₹{totals.totalDiscount.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex justify-between">
                      <span className="font-semibold">Grand Total:</span>
                      <span className="font-semibold text-lg text-green-600">₹{totals.grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Discount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.discountAmount}
                      onChange={(e) => setForm({...form, discountAmount: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Round Off</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.roundOff}
                      onChange={(e) => setForm({...form, roundOff: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {title.includes('Create') ? 'Create Invoice' : 'Update Invoice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// View Invoice Modal Component
const ViewInvoiceModal = ({ invoice, onClose, onDownloadPDF }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold">Invoice Details</h3>
              <p className="text-gray-600">{invoice.invoiceNumber}</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onDownloadPDF(invoice)}
                className="p-2 text-green-600 hover:text-green-800"
                title="Download PDF"
              >
                <Download className="w-5 h-5" />
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
          </div>
          
          {/* Invoice Header */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Company Details</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold">{invoice.companyName || 'Your Company'}</p>
                {invoice.companyAddress && <p className="text-sm text-gray-600">{invoice.companyAddress}</p>}
                {invoice.companyGSTIN && <p className="text-sm text-gray-600">GSTIN: {invoice.companyGSTIN}</p>}
                {invoice.companyEmail && <p className="text-sm text-gray-600">{invoice.companyEmail}</p>}
                {invoice.companyPhone && <p className="text-sm text-gray-600">{invoice.companyPhone}</p>}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Bill To</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold">{invoice.customerName || 'Customer'}</p>
                {invoice.customerEmail && <p className="text-sm text-gray-600">{invoice.customerEmail}</p>}
                {invoice.customerPhone && <p className="text-sm text-gray-600">{invoice.customerPhone}</p>}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Invoice Info</h4>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice #:</span>
                  <span className="font-medium">{invoice.invoiceNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">{new Date(invoice.invoiceDate).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Due Date:</span>
                  <span className="font-medium">{new Date(invoice.dueDate).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${invoice.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {invoice.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${invoice.paymentStatus === 'PAID' ? 'bg-green-100 text-green-800' : invoice.paymentStatus === 'PARTIALLY_PAID' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                    {invoice.paymentStatus}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Items Table */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Items</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">HSN/SAC</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CGST</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SGST</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">IGST</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoice.items?.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2">{item.itemName}</td>
                      <td className="px-4 py-2">{item.hsnSacCode}</td>
                      <td className="px-4 py-2">{item.quantity} {item.unit}</td>
                      <td className="px-4 py-2">₹{item.unitPrice.toFixed(2)}</td>
                      <td className="px-4 py-2">₹{item.cgstAmount?.toFixed(2)} ({item.cgstRate}%)</td>
                      <td className="px-4 py-2">₹{item.sgstAmount?.toFixed(2)} ({item.sgstRate}%)</td>
                      <td className="px-4 py-2">₹{item.igstAmount?.toFixed(2)} ({item.igstRate}%)</td>
                      <td className="px-4 py-2 font-medium">₹{item.lineTotal?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Payment Details</h4>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Mode:</span>
                  <span className="font-medium">{invoice.paymentMode}</span>
                </div>
                {invoice.paymentTransactionId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-medium">{invoice.paymentTransactionId}</span>
                  </div>
                )}
                {invoice.paymentDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Date:</span>
                    <span className="font-medium">{new Date(invoice.paymentDate).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Amount Summary</h4>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₹{invoice.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">CGST:</span>
                  <span className="font-medium">₹{invoice.cgstAmount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">SGST:</span>
                  <span className="font-medium">₹{invoice.sgstAmount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">IGST:</span>
                  <span className="font-medium">₹{invoice.igstAmount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-red-600">-₹{invoice.discountAmount?.toFixed(2)}</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between">
                    <span className="font-semibold">Grand Total:</span>
                    <span className="font-semibold text-lg">₹{invoice.totalAmount?.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-medium text-green-600">₹{invoice.amountPaid?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Balance Due:</span>
                  <span className="font-medium text-red-600">₹{invoice.balanceDue?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Notes and Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {invoice.notes && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">{invoice.notes}</p>
                </div>
              </div>
            )}
            
            {invoice.termsConditions && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Terms & Conditions</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">{invoice.termsConditions}</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ form, setForm, invoice, onClose, onSubmit }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Update Payment</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              Invoice: <span className="font-semibold">{invoice.invoiceNumber}</span><br />
              Total Amount: <span className="font-semibold">₹{invoice.totalAmount?.toLocaleString('en-IN')}</span><br />
              Due: <span className="font-semibold text-red-600">₹{invoice.balanceDue?.toLocaleString('en-IN') || invoice.totalAmount?.toLocaleString('en-IN')}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                <select
                  value={form.paymentMode}
                  onChange={(e) => setForm({...form, paymentMode: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="BANK">Bank Transfer</option>
                  <option value="CASH">Cash</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CARD">Card</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                <select
                  value={form.paymentStatus}
                  onChange={(e) => setForm({...form, paymentStatus: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="PAID">Paid</option>
                  <option value="PARTIALLY_PAID">Partially Paid</option>
                  <option value="UNPAID">Unpaid</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={form.paymentTransactionId}
                  onChange={(e) => setForm({...form, paymentTransactionId: e.target.value})}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.amountPaid}
                  onChange={(e) => setForm({...form, amountPaid: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={form.paymentDate}
                  onChange={(e) => setForm({...form, paymentDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Update Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Share Modal Component
const ShareModal = ({ email, setEmail, invoice, onClose, onSendEmail, onSendWhatsApp }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Share Invoice</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              Share invoice: <span className="font-semibold">{invoice.invoiceNumber}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="customer@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => onSendEmail(invoice, email)}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </button>
                
                <button
                  onClick={() => onSendWhatsApp(invoice)}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  WhatsApp
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Confirm Modal Component
const ConfirmModal = ({ title, message, onConfirm, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{title}</h3>
              <p className="text-gray-600">This action cannot be undone</p>
            </div>
          </div>
          
          <p className="mb-6">{message}</p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;